import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeft, Save, Eye, EyeOff, Download, Users, QrCode, X, Loader2, Clock, Calendar, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { type Mecanica } from "@/lib/fakeApi";
import { queueApiService } from "@/services/queueApi.service";
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import { ModalPortal } from '@/components/ds/ModalPortal';

export default function EventsMecanicaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { toast } = useToast();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<Partial<Mecanica & {
    maxParticipants?: number;
    currentParticipants?: number;
    serviceStartTime?: string | Date;
    estimatedServiceTime?: number;
    simultaneousAttendance?: number;
  }>>({
    nome: '',
    maxParticipants: undefined,
    currentParticipants: 0,
    status: 'fechada',
    termosParticipacao: '',
    serviceStartTime: undefined,
    estimatedServiceTime: 5,
    simultaneousAttendance: 1
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    nome?: string;
    termosParticipacao?: string;
    maxParticipants?: string;
  }>({});
  

  // Helper function to format datetime for input
  const formatDateTimeLocal = (date?: string | Date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper to convert datetime-local value to ISO string in UTC
  const convertDateTimeToISO = (dateTimeLocal: string) => {
    if (!dateTimeLocal) return undefined;
    // datetime-local gives us YYYY-MM-DDTHH:mm format without timezone
    // JavaScript treats this as local time and converts to UTC automatically
    // Example: "2025-09-07T13:00" (1 PM Brazil) → "2025-09-07T16:00:00.000Z" (4 PM UTC)
    return new Date(dateTimeLocal).toISOString();
  };

  useEffect(() => {
    if (isEditing && id) {
      loadMecanica(id);
    }
  }, [id, isEditing]);

  const loadMecanica = async (mecanicaId: string) => {
    try {
      setIsLoading(true);
      const mecanicaData = await queueApiService.getQueue(mecanicaId);
      const capacityData = await queueApiService.getQueueCapacity(mecanicaId);
      
      if (mecanicaData) {
        setFormData({
          ...mecanicaData,
          maxParticipants: capacityData?.max || undefined,
          currentParticipants: capacityData?.current || mecanicaData.quantidadeParticipacao || 0
        });
      } else {
        toast({
          title: "Erro",
          description: "Mecânica não encontrada",
          variant: "destructive",
        });
        navigate(`/mecanicas${location.search}`);
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar a mecânica",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof Mecanica, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset validation errors
    setValidationErrors({});
    let hasErrors = false;
    const errors: typeof validationErrors = {};

    // Validate fields
    if (!formData.nome?.trim()) {
      errors.nome = "O nome da mecânica é obrigatório";
      hasErrors = true;
    }

    if (!formData.termosParticipacao?.trim()) {
      errors.termosParticipacao = "Os termos de participação são obrigatórios";
      hasErrors = true;
    }

    // Validate maxParticipants if set
    if (formData.maxParticipants !== undefined && formData.maxParticipants !== null) {
      if (formData.maxParticipants < 1) {
        errors.maxParticipants = "O limite de participantes deve ser maior que zero";
        hasErrors = true;
      }
      
      // If editing, check if new limit is not below current participants
      if (isEditing && formData.currentParticipants && formData.maxParticipants < formData.currentParticipants) {
        errors.maxParticipants = `O limite não pode ser menor que o número atual de participantes (${formData.currentParticipants})`;
        hasErrors = true;
      }
    }

    // If there are errors, show them and don't submit
    if (hasErrors) {
      setValidationErrors(errors);
      
      // Show toast with first error
      const firstError = errors.nome || errors.termosParticipacao || errors.maxParticipants;
      toast({
        title: "Erro de validação",
        description: firstError,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Prepare data to send (remove currentParticipants as it's read-only)
      const { currentParticipants, ...dataToSend } = formData;
      
      if (isEditing && id) {
        await queueApiService.updateQueue(id, dataToSend);
      } else {
        await queueApiService.createQueue(dataToSend);
      }
      
      toast({
        title: "Sucesso",
        description: isEditing ? "Mecânica atualizada com sucesso!" : "Mecânica criada com sucesso!",
      });
      
      navigate(`/mecanicas${location.search}`);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar a mecânica",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/mecanicas${location.search}`);
  };

  const handleShowQRCode = async () => {
    try {
      // Buscar URL pública da mecânica pela fake API
      let mecanicaUrl = 'https://exemplo.com';
      if (id) {
        const mecanicaData = await queueApiService.getQueue(id);
        mecanicaUrl = mecanicaData?.urlPublica || mecanicaUrl;
      }
      
      // Gerar QR Code
      const qrDataURL = await QRCode.toDataURL(mecanicaUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodeDataURL(qrDataURL);
      setIsQRModalOpen(true);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível gerar o QR Code",
        variant: "destructive",
      });
    }
  };

  const handleDownloadQRCode = async () => {
    if (!qrCodeDataURL) return;

    try {
      // Criar um link temporário para download
      const link = document.createElement('a');
      link.href = qrCodeDataURL;
      link.download = `qr-code-mecanica-${id || 'nova'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Sucesso",
        description: "QR Code baixado com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível baixar o QR Code",
        variant: "destructive",
      });
    }
  };

  const handleDownloadQRCodePDF = async () => {
    if (!qrCodeDataURL) return;

    try {
      const pdf = new jsPDF();
      
      // Adicionar título
      pdf.setFontSize(20);
      pdf.text('QR Code - Mecânica', 105, 30, { align: 'center' });
      
      // Adicionar nome da mecânica
      pdf.setFontSize(14);
      pdf.text(formData.nome || 'Nova Mecânica', 105, 45, { align: 'center' });
      
      // Adicionar QR Code centralizado
      const imgWidth = 100;
      const imgHeight = 100;
      const x = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;
      const y = 60;
      
      pdf.addImage(qrCodeDataURL, 'PNG', x, y, imgWidth, imgHeight);
      
      // Adicionar URL
      pdf.setFontSize(10);
      pdf.text(formData.urlPublica || 'https://exemplo.com', 105, 180, { align: 'center' });
      
      pdf.save(`qr-code-mecanica-${id || 'nova'}.pdf`);
      
      toast({
        title: "Sucesso",
        description: "PDF baixado com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível baixar o PDF",
        variant: "destructive",
      });
    }
  };

  const handleViewParticipantes = () => {
    navigate(`/mecanicas/participantes/${id}?${searchParams.toString()}`);
  };

  if (isLoading && isEditing) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-64 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-muted rounded-xl transition-colors"
          >
            <ArrowLeft className="text-foreground" size={24} />
          </button>
          <div>
            <h1 className="text-[16px] font-bold text-foreground">
              {isEditing ? "Editar Mecânica" : "Nova Mecânica"}
            </h1>
            <p className="text-[13px] text-muted-foreground">
              {isEditing ? "Atualize as informações da mecânica" : "Preencha os dados para criar uma nova mecânica"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isEditing && (
            <button
              onClick={handleViewParticipantes}
              className="btn-primary flex items-center gap-2"
            >
              <Users size={20} />
              Participantes
            </button>
          )}
          <button
            onClick={handleShowQRCode}
            className="btn-primary flex items-center gap-2"
          >
            <QrCode size={20} />
            Ver QR Code
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div 
          className="flex items-center justify-between cursor-pointer mb-6"
          onClick={() => setIsFormCollapsed(!isFormCollapsed)}
        >
          <h3 className="text-[16px] font-semibold text-foreground">Dados da Mecânica</h3>
          {isFormCollapsed ? (
            <Eye className="text-muted-foreground" size={20} />
          ) : (
            <EyeOff className="text-muted-foreground" size={20} />
          )}
        </div>

        {!isFormCollapsed && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nome da Mecânica <span className="text-status-danger">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nome || ''}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder="Digite o nome da mecânica"
                  className={`w-full px-4 py-2 bg-muted border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                    validationErrors.nome 
                      ? 'border-brand-danger focus:ring-brand-danger' 
                      : 'border-border focus:ring-brand-logo-1'
                  }`}
                />
                {validationErrors.nome && (
                  <p className="text-status-danger text-sm mt-1">{validationErrors.nome}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Limite Máximo de Participantes
                </label>
                <input
                  type="number"
                  value={formData.maxParticipants || ''}
                  onChange={(e) => handleInputChange('maxParticipants', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Ilimitado"
                  min="0"
                  className={`w-full px-4 py-2 bg-muted border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                    validationErrors.maxParticipants
                      ? 'border-brand-danger focus:ring-brand-danger'
                      : 'border-border focus:ring-brand-logo-1'
                  }`}
                />
                {validationErrors.maxParticipants ? (
                  <p className="text-status-danger text-sm mt-1">{validationErrors.maxParticipants}</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe vazio para permitir participantes ilimitados
                  </p>
                )}
              </div>

              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Participantes Atuais
                  </label>
                  <div className="w-full px-4 py-2 bg-muted/50 border border-border rounded-ds-card text-muted-foreground">
                    {formData.currentParticipants || 0} participantes
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Este campo é somente leitura
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status da Mecânica
              </label>
              <select
                value={formData.status || 'fechada'}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-logo-1 focus:border-transparent"
              >
                <option value="aberta">Aberta</option>
                <option value="fechada">Fechada</option>
              </select>
            </div>

            {/* Time Settings Section - NEW */}
            <div className="space-y-4 border-t border-border pt-4">
              <h3 className="text-[16px] font-semibold text-foreground flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Configurações de Tempo da Fila
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tempo Médio de Atendimento (minutos)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      value={formData.estimatedServiceTime || 5}
                      onChange={(e) => handleInputChange('estimatedServiceTime', parseInt(e.target.value) || 5)}
                      className="w-32 px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-logo-1 focus:border-transparent"
                    />
                    <span className="text-sm text-muted-foreground">minutos por participante</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tempo médio que cada participante leva para ser atendido
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Horário de Início do Atendimento (Opcional)
                    </span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formatDateTimeLocal(formData.serviceStartTime)}
                    onChange={(e) => handleInputChange('serviceStartTime', convertDateTimeToISO(e.target.value))}
                    min={formatDateTimeLocal(new Date())}
                    className="w-full px-4 py-2 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-logo-1 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Simultaneous Attendance Section */}
              <div className="border-t border-border pt-4 mt-4 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-brand-logo-1" />
                  <h4 className="font-semibold text-foreground">Atendimento Simultâneo</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Quantidade de Atendimentos Simultâneos: {formData.simultaneousAttendance || 1}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="12"
                    value={formData.simultaneousAttendance || 1}
                    onChange={(e) => handleInputChange('simultaneousAttendance', parseInt(e.target.value))}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>1</span>
                    <span>6</span>
                    <span>12</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {formData.simultaneousAttendance === 1
                      ? 'Atendimento individual (padrão)'
                      : `${formData.simultaneousAttendance} pessoas serão atendidas simultaneamente`}
                  </p>
                </div>

                {(formData.simultaneousAttendance || 1) > 1 && (
                  <div className="p-3 bg-brand-attention/10 border border-brand-attention/20 rounded-ds-card flex items-start gap-2">
                    <Info className="w-4 h-4 text-status-attention mt-0.5" />
                    <div className="text-xs text-foreground">
                      <p className="font-semibold mb-1">Atenção:</p>
                      <p>
                        Com {formData.simultaneousAttendance} atendimentos simultâneos, o tempo de espera
                        será reduzido significativamente. Certifique-se de ter equipe suficiente para
                        atender essa quantidade de pessoas ao mesmo tempo.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-2 p-3 bg-brand-logo-1/10 rounded-ds-card flex items-start gap-2">
                <Info className="w-4 h-4 text-brand-logo-1 mt-0.5" />
                <div className="text-xs text-muted-foreground">
                  <p className="font-semibold mb-1 text-foreground">Como funciona o horário agendado?</p>
                  <ul className="space-y-1 ml-2">
                    <li>• Participantes podem entrar na fila antes do horário</li>
                    <li>• O tempo estimado será calculado a partir do horário de início</li>
                    <li>• Exemplo: Início às 17:00, 1º participante: 17:00, 2º: 17:05</li>
                    <li>• Se não preenchido, o atendimento começa imediatamente</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Termos de Participação <span className="text-status-danger">*</span>
              </label>
              <textarea
                value={formData.termosParticipacao || ''}
                onChange={(e) => handleInputChange('termosParticipacao', e.target.value)}
                placeholder="Digite os termos de participação..."
                rows={6}
                className={`w-full px-4 py-2 bg-muted border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent resize-none ${
                  validationErrors.termosParticipacao
                    ? 'border-brand-danger focus:ring-brand-danger'
                    : 'border-border focus:ring-brand-logo-1'
                }`}
              />
              {validationErrors.termosParticipacao && (
                <p className="text-status-danger text-sm mt-1">{validationErrors.termosParticipacao}</p>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <button
                type="button"
                onClick={handleCancel}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    {isEditing ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    {isEditing ? "Atualizar Mecânica" : "Criar Mecânica"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* QR Code Modal */}
      {isQRModalOpen && (
        <ModalPortal><div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/80">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-[16px] font-semibold text-foreground">QR Code da Mecânica</h3>
              <button
                onClick={() => setIsQRModalOpen(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="bg-card p-4 rounded-xl border-2 border-border">
                  {qrCodeDataURL && (
                    <img 
                      src={qrCodeDataURL} 
                      alt="QR Code da Mecânica" 
                      className="w-64 h-64"
                    />
                  )}
                </div>
              </div>
              
              {/* Mechanic Info */}
              <div className="text-center space-y-2">
                <h4 className="font-semibold text-foreground">{formData.nome || 'Nova Mecânica'}</h4>
                <p className="text-sm text-muted-foreground">
                  Escaneie este código para acessar a mecânica
                </p>
                <p className="text-xs text-muted-foreground break-all">
                  {formData.urlPublica || 'https://exemplo.com'}
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setIsQRModalOpen(false)}
                  className="btn-secondary flex-1 justify-center"
                >
                  Fechar
                </button>
                <button
                  onClick={handleDownloadQRCode}
                  className="btn-primary flex-1 flex items-center gap-2 justify-center"
                >
                  <Download size={18} />
                  Baixar Imagem
                </button>
                <button
                  onClick={handleDownloadQRCodePDF}
                  className="btn-primary flex-1 flex items-center gap-2 justify-center"
                >
                  <Download size={18} />
                  Baixar PDF
                </button>
              </div>
            </div>
          </div>
        </div></ModalPortal>
      )}
    </div>
  );
}